import React, { useState, useRef, useCallback, useEffect } from "react";
import { AppShell } from "../components/layout/AppShell";
import { Markdown } from "../components/chat/Markdown";
import { API, TOKEN_KEY, api } from "../lib/api";
import {
  Telescope,
  Upload,
  Film,
  Loader2,
  AlertTriangle,
  RotateCcw,
  Sparkles,
  Play,
  Clock,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Funnel Audit AI — upload a screen recording, get a chronological friction
// audit + conversion diagnostic from Claude Opus 4.8 vision.
// ---------------------------------------------------------------------------

const MAX_FILE_MB = 60;
const ACCEPTED_TYPES = "video/mp4,video/quicktime,video/webm,video/x-m4v";

// Map a storage_path → authenticated proxy URL (uses ?auth=jwt because <img>
// and <video> tags can't send Authorization headers).
function buildAssetUrl(storagePath) {
  const token = localStorage.getItem(TOKEN_KEY) || "";
  return `${API}/funnel-audit/files/${storagePath}?auth=${encodeURIComponent(token)}`;
}

function PhaseLabel({ phase }) {
  const dot = (color) => (
    <span className={`inline-block h-1.5 w-1.5 rounded-full ${color}`} />
  );
  const map = {
    idle: { color: "bg-zinc-500", text: "Ready" },
    uploading: { color: "bg-amber-400 animate-pulse", text: "Uploading & extracting frames…" },
    analyzing: { color: "bg-amber-400 animate-pulse", text: "Claude is reading your funnel…" },
    done: { color: "bg-emerald-500", text: "Audit complete" },
    error: { color: "bg-red-500", text: "Something went wrong" },
  };
  const m = map[phase] || map.idle;
  return (
    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-400">
      {dot(m.color)} {m.text}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const secs = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

function HistoryStatusChip({ status }) {
  const base = "inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border";
  if (status === "analysed") {
    return (
      <span className={`${base} text-emerald-300 border-emerald-500/40 bg-emerald-500/10`}>
        <CheckCircle2 size={10} strokeWidth={2.2} /> Analysed
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className={`${base} text-red-300 border-red-500/40 bg-red-500/10`}>
        <XCircle size={10} strokeWidth={2.2} /> Error
      </span>
    );
  }
  return (
    <span className={`${base} text-zinc-300 border-zinc-500/40 bg-zinc-500/10`}>
      <Clock size={10} strokeWidth={2.2} /> Uploaded
    </span>
  );
}

function HistoryList({ items, loading, onOpen }) {
  if (loading && items.length === 0) {
    return (
      <div className="mt-10 text-center text-zinc-500 text-sm">
        <Loader2 size={16} className="animate-spin inline-block mr-2" /> Loading past audits…
      </div>
    );
  }
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-12" data-testid="funnel-audit-history">
      <div className="flex items-center justify-between mb-4">
        <div className="inline-flex items-center gap-2 text-[#e8c879] text-[11px] uppercase tracking-[0.22em]">
          <Clock size={13} strokeWidth={1.8} /> Past audits
        </div>
        <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-600">
          {items.length} total
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            onClick={() => onOpen(it)}
            data-testid={`funnel-audit-history-${it.id}`}
            className="text-left rounded-xl border border-[#b89846]/30 bg-[#0F0F0E] hover:border-[#d9b35c] hover:-translate-y-0.5 transition-all overflow-hidden flex"
          >
            <div className="shrink-0 w-24 h-24 bg-black/60 border-r border-[#b89846]/20">
              {it.thumbnail_storage_path ? (
                <img
                  src={`${API}/funnel-audit/files/${it.thumbnail_storage_path}?auth=${encodeURIComponent(localStorage.getItem(TOKEN_KEY) || "")}`}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-zinc-700">
                  <Film size={20} strokeWidth={1.8} />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 p-3">
              <div className="text-sm text-white truncate" title={it.original_filename}>
                {it.original_filename || "Recording"}
              </div>
              <div className="mt-1 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                <span>{(it.duration_seconds || 0).toFixed(1)}s</span>
                <span>·</span>
                <span>{it.frame_count || 0} frames</span>
                <span>·</span>
                <span>{formatRelative(it.analysed_at || it.created_at)}</span>
              </div>
              <div className="mt-2">
                <HistoryStatusChip status={it.status} />
              </div>
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function FrameStrip({ frames }) {
  const [broken, setBroken] = useState({});
  if (!frames || frames.length === 0) return null;
  return (
    <div className="mt-5">
      <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-400 mb-3">
        {frames.length} keyframes extracted
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="funnel-audit-frame-strip">
        {frames.map((f) => {
          const isBroken = broken[f.index];
          return (
            <a
              key={f.index}
              href={isBroken ? undefined : buildAssetUrl(f.storage_path)}
              target="_blank"
              rel="noopener noreferrer"
              className={`shrink-0 group ${isBroken ? "pointer-events-none" : ""}`}
              data-testid={`funnel-audit-frame-${f.index}`}
            >
              <div className="h-20 w-32 rounded-lg overflow-hidden border border-[#b89846]/30 bg-black/40 hover:border-[#d9b35c] transition-colors flex items-center justify-center">
                {isBroken ? (
                  <div className="text-[10px] text-zinc-600 uppercase tracking-[0.18em]">unavailable</div>
                ) : (
                  <img
                    src={buildAssetUrl(f.storage_path)}
                    alt={`Frame ${f.index + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={() => setBroken((b) => ({ ...b, [f.index]: true }))}
                  />
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1 text-center font-mono">
                #{String(f.index + 1).padStart(2, "0")}
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

export default function FunnelAudit() {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [audit, setAudit] = useState(null); // { id, frames, report_markdown, duration_seconds, ... }
  const [phase, setPhase] = useState("idle"); // idle | uploading | analyzing | done | error
  const [error, setError] = useState("");
  const [context, setContext] = useState("");
  const [history, setHistory] = useState([]); // past audits for this user
  const [historyLoading, setHistoryLoading] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const resp = await api.get("/funnel-audit", { timeout: 20000 });
      setHistory(resp.data?.items || []);
    } catch (err) {
      // Non-fatal — history failing shouldn't block uploads.
      console.warn("history fetch failed:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Fetch the past-audits list on first mount (and whenever the user
  // explicitly resets after viewing an audit).
  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  const openHistoryItem = useCallback(async (item) => {
    setError("");
    setPhase("done");
    setVideoBroken(false);
    try {
      // Hit the full GET so we get report_markdown + full frames array,
      // not just the thumbnail-only projection from the list endpoint.
      const resp = await api.get(`/funnel-audit/${item.id}`, { timeout: 30000 });
      setAudit(resp.data);
      // If the audit errored last time, show the error state.
      if (resp.data?.status === "error") {
        setPhase("error");
        setError(resp.data?.analysis_error || "Previous analysis failed.");
      } else if (resp.data?.status === "uploaded") {
        // Was uploaded but never analysed — let the user retry.
        setPhase("idle");
      }
      // Scroll to top of result area
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Could not load that audit.";
      toast.error(msg);
      setError(msg);
      setPhase("error");
    }
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setAudit(null);
    setPhase("idle");
    setError("");
    setContext("");
    if (fileInputRef.current) fileInputRef.current.value = "";
    refreshHistory();
  }, [refreshHistory]);

  const onPickFile = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB — max is ${MAX_FILE_MB} MB.`);
      return;
    }
    setFile(f);
    setError("");
  }, []);

  const uploadAndAnalyze = useCallback(async () => {
    if (!file) {
      toast.error("Pick an MP4 first.");
      return;
    }
    setPhase("uploading");
    setError("");

    // (1) Upload + extract frames
    let uploaded;
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const resp = await api.post("/funnel-audit/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      uploaded = resp.data;
      setAudit(uploaded);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed.";
      setError(msg);
      setPhase("error");
      toast.error(msg);
      return;
    }

    // (2) Run Claude vision audit
    setPhase("analyzing");
    try {
      const resp = await api.post(
        `/funnel-audit/${uploaded.id}/analyze`,
        { context: context.trim() },
        { timeout: 300000 }
      );
      setAudit(resp.data);
      setPhase("done");
      toast.success("Funnel audit complete.");
      // Refresh history so the new audit shows up if the user later returns.
      refreshHistory();
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Analysis failed.";
      setError(msg);
      setPhase("error");
      toast.error(msg);
    }
  }, [file, context]);

  const isBusy = phase === "uploading" || phase === "analyzing";

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto py-6 md:py-10 px-4 md:px-6">
        {/* Header */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 text-[#e8c879] mb-3">
            <Telescope size={18} strokeWidth={1.8} />
            <span className="text-[11px] uppercase tracking-[0.28em]">// Funnel Audit AI</span>
          </div>
          <h1 className="text-3xl md:text-4xl text-white font-light leading-tight" data-testid="funnel-audit-title">
            Upload a recording.{" "}
            <span className="text-[#d9b35c] italic font-serif">Get the leaks.</span>
          </h1>
          <p className="text-sm text-zinc-400 mt-3 max-w-2xl leading-relaxed">
            Drop a short screen recording of your store — homepage, product page, cart, or
            full checkout flow. Claude Opus 4.8 will read up to 24 chronological keyframes
            and hand back a UX-friction audit + conversion diagnostic in under a minute.
          </p>
        </div>

        {/* Idle / Upload card */}
        {!audit && (
          <>
          <div className="rounded-2xl border border-[#b89846]/40 bg-gradient-to-br from-[#1a1714] to-[#0c0a08] p-6 md:p-8 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.8)]">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-2xl bg-[#b89846]/15 border border-[#b89846]/30 flex items-center justify-center text-[#e8c879] shrink-0">
                <Film size={22} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <h2 className="text-white text-lg font-medium mb-1">Pick your screen recording</h2>
                <p className="text-zinc-500 text-xs leading-relaxed">
                  MP4, MOV, or WebM. Max {MAX_FILE_MB} MB (~90 seconds at decent quality).
                  We extract up to 24 evenly-spaced keyframes — no need to keep it short.
                </p>
              </div>
            </div>

            <div className="mt-6">
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                onChange={onPickFile}
                className="hidden"
                disabled={isBusy}
                data-testid="funnel-audit-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isBusy}
                data-testid="funnel-audit-pick-button"
                className="w-full rounded-xl border border-dashed border-[#b89846]/40 bg-black/30 hover:bg-black/50 hover:border-[#d9b35c] transition-colors py-10 px-6 text-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={28} className="mx-auto mb-3 text-[#e8c879]" strokeWidth={1.6} />
                <div className="text-white text-sm">
                  {file ? (
                    <>
                      <span className="text-[#d9b35c]">{file.name}</span>{" "}
                      <span className="text-zinc-500">— {(file.size / 1024 / 1024).toFixed(1)} MB</span>
                    </>
                  ) : (
                    "Click to pick a video file"
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-2 uppercase tracking-[0.18em]">
                  or drop it on the area
                </div>
              </button>
            </div>

            {/* Optional context */}
            <div className="mt-5">
              <label
                htmlFor="funnel-audit-context"
                className="text-[11px] uppercase tracking-[0.22em] text-zinc-400"
              >
                Anything to flag for the auditor? (optional)
              </label>
              <textarea
                id="funnel-audit-context"
                data-testid="funnel-audit-context"
                value={context}
                onChange={(e) => setContext(e.target.value)}
                rows={2}
                disabled={isBusy}
                placeholder="e.g. This is our PDP for ₹1,499 ceramic mugs. Most traffic from Meta. COD heavy."
                className="mt-2 w-full rounded-xl bg-black/40 border border-[#b89846]/25 focus:border-[#d9b35c] outline-none text-sm text-white placeholder-zinc-600 px-4 py-3 resize-none"
              />
            </div>

            <button
              type="button"
              onClick={uploadAndAnalyze}
              disabled={!file || isBusy}
              data-testid="funnel-audit-analyze-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#d9b35c] via-[#b89846] to-[#8a6f2a] text-black font-medium py-3.5 px-6 disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
            >
              {phase === "uploading" ? (
                <>
                  <Loader2 size={18} className="animate-spin" strokeWidth={2} />
                  Uploading…
                </>
              ) : phase === "analyzing" ? (
                <>
                  <Loader2 size={18} className="animate-spin" strokeWidth={2} />
                  Analysing keyframes…
                </>
              ) : (
                <>
                  <Sparkles size={18} strokeWidth={1.8} />
                  Run Funnel Audit
                </>
              )}
            </button>

            <div className="mt-4 flex justify-between items-center">
              <PhaseLabel phase={phase} />
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600">
                Powered by Claude Opus 4.8
              </div>
            </div>
          </div>
          <HistoryList items={history} loading={historyLoading} onOpen={openHistoryItem} />
          </>
        )}

        {/* Result card */}
        {audit && (
          <div className="space-y-6">
            {/* Status strip */}
            <div className="flex items-center justify-between rounded-2xl border border-[#b89846]/30 bg-black/40 px-5 py-3">
              <div className="flex items-center gap-3 text-sm text-white">
                <Film size={16} className="text-[#e8c879]" strokeWidth={1.8} />
                <span className="truncate max-w-[40ch]">{audit.original_filename}</span>
                {audit.duration_seconds > 0 && (
                  <span className="text-zinc-500 text-xs">
                    · {audit.duration_seconds.toFixed(1)}s
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={reset}
                data-testid="funnel-audit-reset"
                className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white"
              >
                <RotateCcw size={13} strokeWidth={2} /> New audit
              </button>
            </div>

            {/* Video preview */}
            {audit.mp4_storage_path && !videoBroken && (
              <div className="rounded-2xl overflow-hidden border border-[#b89846]/30 bg-black">
                <video
                  src={buildAssetUrl(audit.mp4_storage_path)}
                  controls
                  preload="metadata"
                  className="w-full max-h-[420px] bg-black"
                  data-testid="funnel-audit-video"
                  onError={() => setVideoBroken(true)}
                >
                  <track kind="captions" />
                </video>
              </div>
            )}
            {audit.mp4_storage_path && videoBroken && (
              <div
                className="rounded-2xl border border-[#b89846]/30 bg-black/40 p-6 text-center"
                data-testid="funnel-audit-video-missing"
              >
                <Film size={26} className="mx-auto mb-2 text-zinc-600" strokeWidth={1.6} />
                <div className="text-sm text-white">
                  Original recording no longer available
                </div>
                <div className="text-[12px] text-zinc-500 mt-1.5 max-w-lg mx-auto leading-relaxed">
                  This audit was uploaded before our storage backend was upgraded. The audit
                  report below is fully intact — re-upload the same recording to bring back the
                  video and per-frame previews.
                </div>
              </div>
            )}

            {/* Frame strip */}
            <div className="rounded-2xl border border-[#b89846]/30 bg-gradient-to-br from-[#1a1714] to-[#0c0a08] p-5">
              <FrameStrip frames={audit.frames} />
            </div>

            {/* Report */}
            <div className="rounded-2xl border border-[#b89846]/40 bg-gradient-to-br from-[#1a1714] to-[#0c0a08] p-6 md:p-8">
              <div className="flex items-center justify-between mb-4">
                <div className="inline-flex items-center gap-2 text-[#e8c879] text-[11px] uppercase tracking-[0.22em]">
                  <Sparkles size={14} strokeWidth={1.8} /> Audit Report
                </div>
                <PhaseLabel phase={phase} />
              </div>

              {phase === "analyzing" && (
                <div className="flex items-center gap-3 text-zinc-400 text-sm py-8 justify-center">
                  <Loader2 size={18} className="animate-spin" strokeWidth={2} />
                  Reading {audit.frames?.length || 24} keyframes…
                </div>
              )}

              {phase === "error" && (
                <div className="flex items-start gap-3 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-200">
                  <AlertTriangle size={18} strokeWidth={2} className="shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium">Analysis failed</div>
                    <div className="text-red-300/80 mt-0.5">{error}</div>
                  </div>
                </div>
              )}

              {audit.report_markdown && (
                <div
                  className="prose prose-invert prose-sm max-w-none prose-headings:text-[#d9b35c] prose-strong:text-white prose-a:text-[#e8c879]"
                  data-testid="funnel-audit-report"
                >
                  <Markdown>{audit.report_markdown}</Markdown>
                </div>
              )}

              {phase === "done" && (
                <div className="mt-6 pt-5 border-t border-[#b89846]/15 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={reset}
                    data-testid="funnel-audit-new"
                    className="inline-flex items-center gap-2 rounded-xl bg-[#b89846]/15 hover:bg-[#b89846]/25 border border-[#b89846]/30 text-[#e8c879] text-sm px-4 py-2.5"
                  >
                    <Play size={14} strokeWidth={1.8} /> Audit another recording
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
